import { NextPage } from "next";
import { useIntl } from "react-intl";
import Head from "next/head";
import Script from "next/script";
import { PageProps } from "@/pages/_app";
import { Layout } from "@/components/layout/Layout";
import { StrapiImage } from "@/utils/strapi";
import { AgencyDetails } from "@/interfaces/agencyDetails";
import { Trips } from "@/interfaces/searchBlock";
import { VehicleType } from "@/components/common/searchBlock/children/vehicleTypeSelection/VehicleTypeSelection";
import { SEO } from "@/interfaces/seo";
import { SEOWithTemplate } from "@/interfaces/seoWithTemplate";
import {
  HeroBlock,
  HeroBlockProps,
} from "@/components/common/heroBlock/HeroBlock";
import { Section } from "@/components/common/section/Section";
import {
  PointOfInterestList,
  PointOfInterestListProps,
} from "@/components/common/pointOfInterestList/PointOfInterestList";
import { CardCarrousel } from "@/components/common/cardCarrousel/CardCarrousel";
import {
  DefaultPresentation,
  DefaultPresentationProps,
} from "@/components/common/defaultPresentation/DefaultPresentation";
import { getNonEmptyString } from "@/utils/getNonEmptyString";
import { FAQ } from "@/components/pages/vehicle/categoryPage/faq/FAQ";
import { FAQProps } from "@/utils/formatCategoryPage";
import { DescriptionWithArticle } from "@/components/common/descriptionWithArticle/DescriptionWithArticle";
import { DescriptionWithArticleProps } from "@/strapiQuery/_interfaces";

import { Feature, isFeatureEnabled } from "@/utils/isFeatureEnabled";
import ReviewsBlock2 from "@/components/common/reviewsBlockV1/ReviewsBlock";
import ReviewsBlock1 from "@/components/common/reviewsBlock/ReviewsBlock";
import { StructuredReviewProps } from "@/components/common/structuredData/StructuredData.interface";
import styles from "./AgencyPage.module.scss";
import { AgencyInformation } from "./agencyInformation/AgencyInformation";

const ReviewsBlock = ({
  name,
  agency,
  rentACarReview,
  reviewsConst,
  image,
}: StructuredReviewProps) => {
  if (!isFeatureEnabled(Feature.NEW_REVIEWS_BLOCK)) {
    return (
      <ReviewsBlock1
        name={name}
        agency={agency}
        rentACarReview={rentACarReview}
        reviewsConst={reviewsConst}
        image={image}
      />
    );
  } else {
    return (
      <ReviewsBlock2
        name={name}
        agency={agency}
        rentACarReview={rentACarReview}
        reviewsConst={reviewsConst}
        image={image}
      />
    );
  }
};

export type AgencyPageContent = {
  slug: string;
  pointOfInterestList: PointOfInterestListProps | undefined;
  agencyDescription: DescriptionWithArticleProps[] | undefined;
  faq: FAQProps | undefined;
  heroBlock?: HeroBlockProps;
  seo: SEO;
  seoWithTemplate: SEOWithTemplate;
};
type AgencyPageProps = PageProps &
  AgencyPageContent & {
    categoriesImages: {
      [name: string]: StrapiImage[] | undefined;
    };
    agency: AgencyDetails;
    defaultPresentation: DefaultPresentationProps;
    defaultCanonicalRoute: string;
  };

const hostUrl = process.env.NEXT_PUBLIC_HOST_URL ?? "";

export const AgencyPage: NextPage<AgencyPageProps> = ({
  layout,
  reviewsConst,
  slug,
  agency,
  defaultPresentation,
  pointOfInterestList,
  agencyDescription,
  faq,
  heroBlock,
  seo,
  seoWithTemplate,
  defaultCanonicalRoute,
}) => {
  const intl = useIntl();
  const departmentBreadcumbURL =
    agency.state.slug !== "75"
      ? `${hostUrl}/agences/departement/${agency.state.slug}`
      : "#";

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: intl.formatMessage({ id: "home" }),
                  item: hostUrl,
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: intl.formatMessage({ id: "agenciesFrance" }),
                  item: `${hostUrl}/agences`,
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: agency.state.name,
                  item: `${hostUrl}/agences/departement/${agency.state.slug}`,
                },
                {
                  "@type": "ListItem",
                  position: 4,
                  name: agency.city.name,
                  item: `${hostUrl}/agences/v/${agency.city.slug}`,
                },
                {
                  "@type": "ListItem",
                  position: 5,
                  name: agency.label,
                  item: `${hostUrl}/agences/${slug}`,
                },
              ],
            }),
          }}
        />
      </Head>
      <Layout
        layout={layout}
        pageMeta={seo}
        title={
          seoWithTemplate.overrideTitle ??
          intl.formatMessage(
            { id: "defaultMetaTitle" },
            {
              name: getNonEmptyString(
                seoWithTemplate.valueInTemplateTitle,
                agency.label
              ),
            }
          )
        }
        description={
          seoWithTemplate.overrideDescription ??
          intl.formatMessage(
            { id: "defaultMetaDescription" },
            {
              name: getNonEmptyString(
                seoWithTemplate.valueInTemplateDescription,
                agency.label
              ),
              additional: agency.state.name,
            }
          )
        }
        breadcrumbs={[
          { title: intl.formatMessage({ id: "home" }), url: "/" },
          {
            title: intl.formatMessage({ id: "agenciesFrance" }),
            url: `${hostUrl}/agences`,
          },
          {
            title: agency.state.name,
            url: departmentBreadcumbURL,
          },
          {
            title: agency.city.name,
            url: `${hostUrl}/agences/v/${agency.city.slug}`,
          },
          {
            title: agency.label,
            url: `${hostUrl}/agences/${slug}`,
          },
        ]}
        defaultCanonicalRoute={defaultCanonicalRoute}
      >
        <Script
          id="insider-agency-page"
          strategy="beforeInteractive"
          dangerouslySetInnerHTML={{
            __html: `
          window.insider_object = window.insider_object || {};
          window.insider_object.page = {
              "type": "Content"
          }
          `,
          }}
        />
        <HeroBlock
          title={
            heroBlock?.title ??
            intl.formatMessage(
              { id: "agencyPageTitle" },
              {
                name: agency.label,
                br: () => <br />,
              }
            )
          }
          searchBlockInitialState={{
            vehicleType: VehicleType.CAR,
            isOneWayTrip: false,
            [Trips.ROUND_TRIP]: {
              description: agency.label,
              isPrefilled: true,
              latitude: agency.addressDto.latitude.toString(),
              longitude: agency.addressDto.longitude.toString(),
              isAgency: true,
            },
          }}
        />

        <AgencyInformation agency={agency} />

        <Section>
          {pointOfInterestList &&
            pointOfInterestList.pointOfInterests.length > 0 && (
              <PointOfInterestList
                location={agency.label}
                pointOfInterestList={pointOfInterestList}
              />
            )}
          {(!pointOfInterestList ||
            pointOfInterestList.pointOfInterests.length === 0) && (
            <DefaultPresentation {...defaultPresentation} name={agency.label} />
          )}
          {agencyDescription?.map((cat, index: number) => (
            <DescriptionWithArticle {...cat} key={index} />
          ))}
          <div className={styles.divider}></div>
          {agency.agenciesByProximity.length > 0 && (
            <CardCarrousel
              title={intl.formatMessage(
                { id: "proximityAgenciesTitle" },
                { agencyName: agency.label }
              )}
              urlPrefix={`${hostUrl}/agences/`}
              cards={agency.agenciesByProximity}
            />
          )}
        </Section>

        {faq?.questionsAndAnswers && (
          <FAQ
            title={faq.title}
            questionsAndAnswers={faq.questionsAndAnswers}
          />
        )}

        {agency.reviews?.clientReviews &&
          agency.reviews.clientReviews.length && (
            <ReviewsBlock
              name={agency.label}
              agency={agency}
              rentACarReview={agency.reviews}
              reviewsConst={reviewsConst ?? undefined}
            />
          )}
      </Layout>
    </>
  );
};
